import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, MapPin, Calendar, Clock, Star, ChevronRight, User, LogOut, Menu, X,
  Phone, Mail, CheckCircle, AlertCircle, Heart, Stethoscope, Eye, Bone,
  Baby, Brain, Ear, Activity, Plus, ArrowLeft, Filter, MessageCircle, Share2, ShieldCheck
} from "lucide-react";
import axios from "axios";
import "@/App.css";
import PrivacyPage from "./pages/PrivacyPage";
import LegalPage from "./pages/LegalPage";
import DeleteAccountPage from "./pages/DeleteAccountPage";
import TermsPage from "./pages/TermsPage";
import SupportPage from "./pages/SupportPage";
import SmartAppBanner from "./components/SmartAppBanner";
import MessagesPage from "./pages/MessagesPage";
import { Logo } from "./components/Logo";
import { PageSEO, buildPhysicianSchema } from "./components/PageSEO";
import { DirectionsButton, CopyAddressButton } from "./components/DirectionsButton";
import { AppDownloadBadges } from "./components/AppDownloadBadges";
import { ShareDoctorModal } from "./components/ShareDoctorModal";
import { CallbackRequestModal, MyCallbackRequests } from "./components/CallbackPatient";
import CookieBanner from "./components/CookieBanner";
import { setupErrorReporter } from "./lib/errorReporter";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Set up global error reporter ONCE at app startup (fire-and-forget email alerts)
setupErrorReporter();

// Configure axios defaults at startup
const storedToken = localStorage.getItem("doctodays_token");
if (storedToken) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
}

// ==================== CONTEXT ====================
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("doctodays_token"));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem("doctodays_token");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/me`);
      setUser(res.data);
    } catch (err) {
      console.error("Auth check failed:", err);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const res = await axios.post(`${API_URL}/auth/login`, { email, password });
    const accessToken = res.data.access_token;
    localStorage.setItem("doctodays_token", accessToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
    setToken(accessToken);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API_URL}/auth/register`, data);
    const accessToken = res.data.access_token;
    localStorage.setItem("doctodays_token", accessToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
    setToken(accessToken);
    setUser(res.data.user);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Logo Component
// ==================== COMPONENTS ====================
const Navbar = () => {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center" data-testid="logo">
            <Logo size="md" withText textClassName="text-xl hidden sm:inline" />
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/search" className="text-slate-600 hover:text-emerald-600 transition-colors" data-testid="nav-search">
              Trouver un praticien
            </Link>
            {user ? (
              <>
                <Link to="/dashboard" className="text-slate-600 hover:text-emerald-600 transition-colors" data-testid="nav-dashboard">
                  Mes rendez-vous
                </Link>
                <Link to="/messages" className="text-slate-600 hover:text-emerald-600 transition-colors" data-testid="nav-messages">
                  Messages
                </Link>
                <div className="flex items-center gap-3">
                  <span className="text-slate-700 font-medium">{user.first_name}</span>
                  <button
                    onClick={logout}
                    className="p-2 text-slate-500 hover:text-red-500 transition-colors"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              </>
            ) : (
              <Link
                to="/login"
                className="bg-emerald-600 text-white px-6 py-2.5 rounded-full font-medium hover:bg-emerald-700 transition-all hover:scale-105"
                data-testid="login-btn"
              >
                Se connecter
              </Link>
            )}
          </nav>

          <button className="md:hidden p-2" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t"
          >
            <div className="px-4 py-4 space-y-3">
              <Link to="/search" className="block py-2 text-slate-700" onClick={() => setMenuOpen(false)}>
                Trouver un praticien
              </Link>
              {user ? (
                <>
                  <Link to="/dashboard" className="block py-2 text-slate-700" onClick={() => setMenuOpen(false)}>
                    Mes rendez-vous
                  </Link>
                  <Link to="/messages" className="block py-2 text-slate-700" onClick={() => setMenuOpen(false)}>
                    Messages
                  </Link>
                  <button onClick={() => { logout(); setMenuOpen(false); }} className="text-red-500">
                    Déconnexion
                  </button>
                </>
              ) : (
                <Link to="/login" className="block py-2 text-emerald-600 font-medium" onClick={() => setMenuOpen(false)}>
                  Se connecter
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => (
  <footer className="bg-slate-900 text-white py-12 mt-auto">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Logo size="small" />
            <span className="text-lg font-bold">Doctodays</span>
          </div>
          <p className="text-slate-400 text-sm mb-4">La santé accessible à tous, simplement.</p>
          <AppDownloadBadges variant="compact" />
        </div>
        <div>
          <h4 className="font-semibold mb-3">Patients</h4>
          <ul className="space-y-2 text-slate-400 text-sm">
            <li><Link to="/search" className="hover:text-white transition-colors">Trouver un médecin</Link></li>
            <li><Link to="/dashboard" className="hover:text-white transition-colors">Mes rendez-vous</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-3">Soignants</h4>
          <ul className="space-y-2 text-slate-400 text-sm">
            <li><Link to="/practitioner/login" className="hover:text-white transition-colors">Espace soignant</Link></li>
            <li><Link to="/practitioner/login" className="hover:text-white transition-colors">Inscription praticien</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-3">Contact</h4>
          <ul className="space-y-2 text-slate-400 text-sm">
            <li className="flex items-center gap-2"><Mail className="w-4 h-4" /> contact@doctodays.com</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-500 text-sm">
        © 2026 Doctodays. Tous droits réservés.
      </div>
    </div>
  </footer>
);

// ==================== PAGES ====================
const HomePage = () => {
  const [specialty, setSpecialty] = useState("");
  const [city, setCity] = useState("");
  const [specialties, setSpecialties] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_URL}/specialties`).then(res => setSpecialties(res.data.specialties));
    // Seed database on first load
    axios.post(`${API_URL}/seed`).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/search?specialty=${encodeURIComponent(specialty)}&city=${encodeURIComponent(city)}`);
  };

  const popularSpecialties = [
    { name: "Médecin généraliste", icon: <Stethoscope className="w-6 h-6" /> },
    { name: "Dentiste", icon: <Heart className="w-6 h-6" /> },
    { name: "Dermatologue", icon: <Activity className="w-6 h-6" /> },
    { name: "Ophtalmologue", icon: <Eye className="w-6 h-6" /> },
    { name: "Kinésithérapeute", icon: <Bone className="w-6 h-6" /> },
    { name: "Pédiatre", icon: <Baby className="w-6 h-6" /> },
  ];

  return (
    <div className="min-h-screen">
      <PageSEO
        title="Prenez rendez-vous avec un médecin en ligne"
        description="DoctoDays vous permet de trouver un médecin (généraliste, dentiste, dermatologue...) et de prendre rendez-vous 24h/24 en quelques clics. Service 100% gratuit pour les patients."
        canonical="https://doctodays.com/"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-[600px] md:min-h-[700px] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=1920&q=80" 
            alt="Consultation médicale"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/70" />
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-50/50 to-transparent" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
              Prenez rendez-vous avec un{" "}
              <span className="text-emerald-600">professionnel de santé</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mb-10">
              Trouvez rapidement un praticien près de chez vous et réservez votre consultation en quelques clics.
            </p>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl shadow-slate-200/50 p-3 md:p-4" data-testid="search-form">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <select
                    value={specialty}
                    onChange={(e) => setSpecialty(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all appearance-none bg-slate-50"
                    data-testid="specialty-select"
                  >
                    <option value="">Spécialité ou praticien</option>
                    {specialties.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="flex-1 relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Ville ou code postal"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all bg-slate-50"
                    data-testid="city-input"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-emerald-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-emerald-700 transition-all hover:scale-[1.02] shadow-lg shadow-emerald-600/30"
                  data-testid="search-btn"
                >
                  Rechercher
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Popular Specialties */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center">
            Spécialités les plus recherchées
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {popularSpecialties.map((spec, i) => (
              <motion.div
                key={spec.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Link
                  to={`/search?specialty=${encodeURIComponent(spec.name)}`}
                  className="group flex flex-col items-center p-6 bg-white rounded-2xl border border-slate-100 hover:border-emerald-200 hover:shadow-lg hover:shadow-emerald-900/5 transition-all"
                  data-testid={`specialty-card-${i}`}
                >
                  <div className="w-14 h-14 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600 group-hover:bg-emerald-100 transition-colors mb-3">
                    {spec.icon}
                  </div>
                  <span className="text-sm font-medium text-slate-700 text-center">{spec.name}</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <Calendar className="w-8 h-8" />, title: "Réservation instantanée", desc: "Prenez rendez-vous 24h/24, 7j/7 en quelques clics" },
              { icon: <Clock className="w-8 h-8" />, title: "Rappels automatiques", desc: "Recevez des rappels pour ne jamais oublier vos rendez-vous" },
              { icon: <CheckCircle className="w-8 h-8" />, title: "Confirmation immédiate", desc: "Votre rendez-vous est confirmé instantanément" },
            ].map((feat, i) => (
              <div key={i} className="text-center p-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600 mx-auto mb-4">
                  {feat.icon}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feat.title}</h3>
                <p className="text-slate-600">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Download Section */}
      <AppDownloadBadges variant="section" />
    </div>
  );
};

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [specialties, setSpecialties] = useState([]);
  const [cities, setCities] = useState([]);
  const [filters, setFilters] = useState({
    specialty: searchParams.get("specialty") || "",
    city: searchParams.get("city") || "",
  });

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/specialties`),
      axios.get(`${API_URL}/cities`)
    ]).then(([specRes, cityRes]) => {
      setSpecialties(specRes.data.specialties);
      setCities(cityRes.data.cities);
    });
  }, []);

  useEffect(() => {
    fetchDoctors();
  }, [filters]);

  const fetchDoctors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.specialty) params.append("specialty", filters.specialty);
      if (filters.city) params.append("city", filters.city);
      const res = await axios.get(`${API_URL}/doctors?${params}`);
      setDoctors(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <PageSEO
        title={
          filters.specialty && filters.city
            ? `${filters.specialty} à ${filters.city}`
            : filters.specialty
              ? `${filters.specialty} — Prendre rendez-vous`
              : "Rechercher un médecin"
        }
        description={
          filters.specialty && filters.city
            ? `Trouvez un(e) ${filters.specialty} à ${filters.city} et prenez rendez-vous en ligne sur DoctoDays. Créneaux disponibles, tarifs et avis.`
            : "Recherchez un professionnel de santé par spécialité et par ville, et prenez rendez-vous en ligne 24h/24 sur DoctoDays."
        }
        canonical={
          filters.specialty || filters.city
            ? `https://doctodays.com/search?specialty=${encodeURIComponent(filters.specialty)}&city=${encodeURIComponent(filters.city)}`
            : "https://doctodays.com/search"
        }
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <select
                value={filters.specialty}
                onChange={(e) => setFilters(f => ({ ...f, specialty: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none"
                data-testid="filter-specialty"
              >
                <option value="">Toutes les spécialités</option>
                {specialties.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex-1">
              <select
                value={filters.city}
                onChange={(e) => setFilters(f => ({ ...f, city: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none"
                data-testid="filter-city"
              >
                <option value="">Toutes les villes</option>
                {cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-slate-900">
            {loading ? "Recherche en cours..." : `${doctors.length} praticien${doctors.length > 1 ? "s" : ""} trouvé${doctors.length > 1 ? "s" : ""}`}
          </h1>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                <div className="flex gap-4">
                  <div className="w-20 h-20 bg-slate-200 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                    <div className="h-3 bg-slate-200 rounded w-1/2" />
                    <div className="h-3 bg-slate-200 rounded w-2/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {doctors.map((doctor, i) => (
              <DoctorCard key={doctor.id} doctor={doctor} index={i} />
            ))}
          </div>
        )}

        {!loading && doctors.length === 0 && (
          <div className="text-center py-16">
            <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">Aucun praticien trouvé</h3>
            <p className="text-slate-500">Essayez de modifier vos critères de recherche</p>
          </div>
        )}
      </div>
    </div>
  );
};

const DoctorCard = ({ doctor, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05 }}
  >
    <Link
      to={`/doctor/${doctor.id}`}
      className="group block bg-white rounded-2xl border border-slate-100 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-900/5 transition-all overflow-hidden"
      data-testid={`doctor-card-${index}`}
    >
      <div className="p-6">
        <div className="flex gap-4">
          <img
            src={doctor.photo_url}
            alt={`Dr. ${doctor.first_name} ${doctor.last_name}`}
            className="w-20 h-20 rounded-xl object-cover"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 group-hover:text-emerald-600 transition-colors">
              Dr. {doctor.first_name} {doctor.last_name}
            </h3>
            <p className="text-emerald-600 text-sm font-medium">{doctor.specialty}</p>
            <div className="flex items-center gap-1 mt-1">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              <span className="text-sm font-medium text-slate-700">{doctor.rating}</span>
              <span className="text-sm text-slate-400">({doctor.review_count} avis)</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-4 text-sm text-slate-500">
          <MapPin className="w-4 h-4" />
          <span className="truncate">{doctor.address}</span>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
          <div>
            <span className="text-xs text-slate-500">Prochaine dispo</span>
            <p className="text-emerald-600 font-medium text-sm">{doctor.next_available}</p>
          </div>
          <span className="text-emerald-600 font-semibold">{doctor.consultation_fee} €</span>
        </div>
      </div>
    </Link>
  </motion.div>
);

/**
 * Slug-based doctor route /dr/:slug.
 *
 * Resolves the slug via the backend API and then reuses the canonical
 * DoctorPage by injecting the doctor id via React Router state.
 */
const DoctorBySlugPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [resolving, setResolving] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/doctors/by-slug/${encodeURIComponent(slug)}`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.id) {
          // Render in place using the legacy DoctorPage by navigating to its route.
          // We use replace so the friendly URL stays in history.
          navigate(`/doctor/${res.data.id}`, { replace: false, state: { fromSlug: true } });
        } else {
          setNotFound(true);
        }
      })
      .catch(() => !cancelled && setNotFound(true))
      .finally(() => !cancelled && setResolving(false));
    return () => { cancelled = true; };
  }, [slug, navigate]);

  if (resolving) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto" />
          <p className="mt-4 text-slate-500">Chargement du praticien…</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Praticien introuvable</h1>
          <p className="text-slate-600 mb-6">
            Ce lien ne correspond à aucun praticien actif sur DoctoDays.
          </p>
          <button
            onClick={() => navigate("/search")}
            className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors"
          >
            Rechercher un autre praticien
          </button>
        </div>
      </div>
    );
  }
  return null;
};

const DoctorPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [booking, setBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [consultationType, setConsultationType] = useState("cabinet");
  const [callbackOpen, setCallbackOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/doctors/${id}`),
      axios.get(`${API_URL}/reviews/${id}`)
    ]).then(([docRes, revRes]) => {
      setDoctor(docRes.data);
      setReviews(revRes.data);
    }).finally(() => setLoading(false));
  }, [id]);

  const handleBook = async () => {
    const token = localStorage.getItem("doctodays_token");
    if (!token) {
      navigate("/login?redirect=/doctor/" + id);
      return;
    }
    if (!selectedDate || !selectedTime) return;

    setBooking(true);
    try {
      const response = await axios.post(`${API_URL}/appointments`, {
        doctor_id: id,
        date: selectedDate,
        time: selectedTime,
        consultation_type: consultationType,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      console.log("Booking success:", response.data);
      setBookingSuccess(true);
      setTimeout(() => navigate("/dashboard"), 2000);
    } catch (err) {
      console.error("Booking error:", err);
      if (err.response?.status === 401) {
        navigate("/login?redirect=/doctor/" + id);
        return;
      }
      const errorMessage = err.response?.data?.detail || "Erreur lors de la réservation";
      alert(errorMessage);
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-5xl mx-auto px-4 animate-pulse">
          <div className="bg-white rounded-2xl p-8">
            <div className="flex gap-6">
              <div className="w-32 h-32 bg-slate-200 rounded-2xl" />
              <div className="flex-1 space-y-3">
                <div className="h-6 bg-slate-200 rounded w-1/3" />
                <div className="h-4 bg-slate-200 rounded w-1/4" />
                <div className="h-4 bg-slate-200 rounded w-1/2" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Praticien non trouvé</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <PageSEO
        title={`${doctor.specialty || "Praticien"} à ${doctor.city || ""} – Dr. ${doctor.first_name || ""} ${doctor.last_name || ""}`.trim()}
        description={`Prenez rendez-vous en ligne avec le Dr. ${doctor.first_name || ""} ${doctor.last_name || ""}, ${doctor.specialty || "professionnel de santé"}${doctor.city ? ` à ${doctor.city}` : ""}. Consultation à partir de ${doctor.consultation_fee ?? "—"} €. Disponibilités et avis sur DoctoDays.`}
        canonical={`https://doctodays.com/doctor/${doctor.id}`}
        image={doctor.photo_url}
        structuredData={buildPhysicianSchema(doctor, reviews)}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/search" className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-600 mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour aux résultats
        </Link>

        {bookingSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 flex items-center gap-3"
          >
            <CheckCircle className="w-6 h-6 text-emerald-600" />
            <div>
              <p className="font-semibold text-emerald-800">Rendez-vous confirmé !</p>
              <p className="text-emerald-600 text-sm">Redirection vers vos rendez-vous...</p>
            </div>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Doctor Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl p-6 md:p-8">
              <div className="flex flex-col md:flex-row gap-6">
                <img
                  src={doctor.photo_url}
                  alt={`Dr. ${doctor.first_name} ${doctor.last_name}`}
                  className="w-32 h-32 rounded-2xl object-cover"
                />
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h1 className="text-2xl font-bold text-slate-900" data-testid="doctor-name">
                        Dr. {doctor.first_name} {doctor.last_name}
                      </h1>
                      <p className="text-emerald-600 font-medium text-lg">{doctor.specialty}</p>
                    </div>
                    <button
                      onClick={() => setShareOpen(true)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl font-medium text-sm transition-colors border border-emerald-200 flex-shrink-0"
                      data-testid="share-doctor-btn"
                      aria-label="Partager le profil du praticien"
                    >
                      <Share2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Partager</span>
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center gap-1">
                      <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                      <span className="font-semibold">{doctor.rating}</span>
                    </div>
                    <span className="text-slate-400">•</span>
                    <span className="text-slate-600">{doctor.review_count} avis</span>
                  </div>
                  <div className="flex items-center gap-2 mt-3 text-slate-600">
                    <MapPin className="w-4 h-4" />
                    <span>{doctor.address}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-slate-600">
                    <Phone className="w-4 h-4" />
                    <span>{doctor.phone}</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t">
                <h3 className="font-semibold text-slate-900 mb-2">À propos</h3>
                <p className="text-slate-600">{doctor.bio}</p>
              </div>

              <div className="mt-4 flex items-center gap-2">
                <span className="text-slate-500">Tarif consultation:</span>
                <span className="font-semibold text-emerald-600">{doctor.consultation_fee} €</span>
              </div>
            </div>

            {/* Reviews */}
            <div className="bg-white rounded-2xl p-6 md:p-8">
              <h3 className="font-semibold text-slate-900 mb-4">Avis patients ({reviews.length})</h3>
              {reviews.length > 0 ? (
                <div className="space-y-4">
                  {reviews.slice(0, 5).map(review => (
                    <div key={review.id} className="border-b border-slate-100 pb-4 last:border-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-medium text-sm">
                          {review.patient_name[0]}
                        </div>
                        <span className="font-medium text-slate-700">{review.patient_name}</span>
                        <div className="flex items-center gap-1 ml-auto">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${i < review.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-slate-600 text-sm">{review.comment}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">Aucun avis pour le moment</p>
              )}
            </div>
          </div>

          {/* Booking Widget */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 sticky top-24">
              <h3 className="font-semibold text-slate-900 mb-4">Prendre rendez-vous</h3>

              {doctor.phone_appointments_enabled && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-slate-600 mb-2">Type de consultation</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setConsultationType("cabinet")}
                      className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${
                        consultationType === "cabinet"
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"
                      }`}
                      data-testid="consult-type-cabinet"
                    >
                      <MapPin className="w-4 h-4" /> Au cabinet
                    </button>
                    <button
                      onClick={() => setConsultationType("telephone")}
                      className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${
                        consultationType === "telephone"
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"
                      }`}
                      data-testid="consult-type-telephone"
                    >
                      <Phone className="w-4 h-4" /> Par téléphone
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {doctor.available_slots?.slice(0, 5).map(slot => (
                  <div key={slot.date} className="border rounded-xl p-3">
                    <div className="font-medium text-slate-700 mb-2">
                      {slot.day_name} {new Date(slot.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {slot.times.map(time => (
                        <button
                          key={`${slot.date}-${time}`}
                          onClick={() => { setSelectedDate(slot.date); setSelectedTime(time); }}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            selectedDate === slot.date && selectedTime === time
                              ? "bg-emerald-600 text-white"
                              : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          }`}
                          data-testid={`slot-${slot.date}-${time}`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleBook}
                disabled={!selectedDate || !selectedTime || booking}
                className="w-full mt-6 bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="book-btn"
              >
                {booking ? "Réservation..." : "Confirmer le rendez-vous"}
              </button>

              {doctor.callback_enabled && (
                <button
                  onClick={() => {
                    const token = localStorage.getItem("doctodays_token");
                    if (!token) {
                      navigate("/login?redirect=/doctor/" + id);
                      return;
                    }
                    setCallbackOpen(true);
                  }}
                  className="w-full mt-3 bg-emerald-50 text-emerald-700 border border-emerald-200 py-3 rounded-xl font-semibold hover:bg-emerald-100 transition-colors flex items-center justify-center gap-2"
                  data-testid="request-callback-btn"
                >
                  <Phone className="w-4 h-4" /> Demander un rappel téléphonique
                </button>
              )}

              {selectedDate && selectedTime && (
                <p className="text-center text-sm text-slate-500 mt-2">
                  {new Date(selectedDate).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} à {selectedTime}
                  {consultationType === "telephone" && doctor.phone_appointments_enabled ? " — par téléphone" : ""}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <CallbackRequestModal
        doctor={doctor}
        open={callbackOpen}
        onClose={() => setCallbackOpen(false)}
        defaultPhone={user?.phone}
      />

      <ShareDoctorModal
        doctor={doctor}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        apiBase={process.env.REACT_APP_BACKEND_URL}
        siteBase={typeof window !== 'undefined' ? window.location.origin : ''}
      />
    </div>
  );
};

const LoginPage = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    phone: "",
  });
  const [acceptTerms, setAcceptTerms] = useState(false);

  const redirect = searchParams.get("redirect") || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (isRegister && !acceptTerms) {
      setError("Vous devez accepter les CGU et la politique de confidentialité.");
      return;
    }

    setLoading(true);

    try {
      if (isRegister) {
        await register({ ...form, accept_terms: true });
      } else {
        await login(form.email, form.password);
      }
      navigate(redirect);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Side */}
      <div className="hidden lg:flex lg:w-1/2 bg-emerald-600 p-12 flex-col justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Plus className="w-6 h-6 text-white" strokeWidth={3} />
          </div>
          <span className="text-xl font-bold text-white">Doctodays</span>
        </div>
        <div>
          <h2 className="text-4xl font-bold text-white mb-4">
            Votre santé, notre priorité
          </h2>
          <p className="text-emerald-100 text-lg">
            Rejoignez des milliers de patients qui font confiance à Doctodays pour leurs rendez-vous médicaux.
          </p>
        </div>
        <div className="text-emerald-200 text-sm">
          © 2024 Doctodays. Tous droits réservés.
        </div>
      </div>

      {/* Right Side */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Plus className="w-6 h-6 text-white" strokeWidth={3} />
            </div>
            <span className="text-xl font-bold text-slate-900">Doctodays</span>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {isRegister ? "Créer un compte" : "Connexion"}
          </h1>
          <p className="text-slate-600 mb-8">
            {isRegister ? "Inscrivez-vous pour prendre rendez-vous" : "Connectez-vous à votre compte"}
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            {isRegister && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prénom</label>
                  <input
                    type="text"
                    required
                    value={form.first_name}
                    onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
                    data-testid="input-firstname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
                  <input
                    type="text"
                    required
                    value={form.last_name}
                    onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
                    data-testid="input-lastname"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
                data-testid="input-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe</label>
              <input
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
                data-testid="input-password"
              />
            </div>

            {isRegister && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone (optionnel)</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
                  data-testid="input-phone"
                />
              </div>
            )}

            {isRegister && (
              <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer" data-testid="consent-checkbox-wrapper">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  data-testid="consent-checkbox"
                />
                <span>
                  J'accepte les{" "}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline font-medium">
                    Conditions Générales d'Utilisation
                  </Link>
                  {" "}et la{" "}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline font-medium">
                    politique de confidentialité
                  </Link>
                  .<span className="text-red-500"> *</span>
                </span>
              </label>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 transition-all disabled:opacity-50"
              data-testid="submit-btn"
            >
              {loading ? "Chargement..." : (isRegister ? "S'inscrire" : "Se connecter")}
            </button>

            {!isRegister && (
              <div className="text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-slate-500 hover:text-emerald-600"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
            )}
          </form>

          <p className="text-center mt-6 text-slate-600">
            {isRegister ? "Déjà un compte ?" : "Pas encore de compte ?"}{" "}
            <button
              onClick={() => { setIsRegister(!isRegister); setError(""); }}
              className="text-emerald-600 font-medium hover:underline"
              data-testid="toggle-auth"
            >
              {isRegister ? "Se connecter" : "S'inscrire"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

// ==================== FORGOT PASSWORD PAGE ====================
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.post(`${API_URL}/auth/forgot-password`, {
        email,
        account_type: "patient"
      });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="large" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Mot de passe oublié</h1>
          <p className="text-slate-600 mt-2">
            {sent ? "Email envoyé !" : "Entrez votre email pour réinitialiser votre mot de passe"}
          </p>
        </div>

        {sent ? (
          <div className="text-center space-y-6">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <Mail className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <p className="text-slate-600">
                Si un compte existe avec l'adresse <strong>{email}</strong>, vous recevrez un email avec un lien de réinitialisation.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Vérifiez également votre dossier spam.
              </p>
            </div>
            <Link
              to="/login"
              className="inline-block px-6 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700"
            >
              Retour à la connexion
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none"
                placeholder="votre@email.com"
                data-testid="forgot-email"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 transition-all disabled:opacity-50"
              data-testid="forgot-submit"
            >
              {loading ? "Envoi..." : "Envoyer le lien de réinitialisation"}
            </button>

            <div className="text-center">
              <Link to="/login" className="text-slate-600 hover:text-emerald-600 text-sm">
                ← Retour à la connexion
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ==================== RESET PASSWORD PAGE ====================
const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Lien invalide</h1>
          <p className="text-slate-600 mb-6">Ce lien de réinitialisation est invalide ou a expiré.</p>
          <Link to="/forgot-password" className="text-emerald-600 hover:underline">
            Demander un nouveau lien
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="large" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">
            {success ? "Mot de passe réinitialisé !" : "Nouveau mot de passe"}
          </h1>
        </div>

        {success ? (
          <div className="text-center space-y-6">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-slate-600">
              Votre mot de passe a été modifié avec succès.<br />
              Redirection vers la connexion...
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Nouveau mot de passe</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none"
                placeholder="••••••••"
                minLength={6}
                data-testid="new-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Confirmer le mot de passe</label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none"
                placeholder="••••••••"
                minLength={6}
                data-testid="confirm-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 transition-all disabled:opacity-50"
              data-testid="reset-submit"
            >
              {loading ? "Réinitialisation..." : "Réinitialiser le mot de passe"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState({ open: false, appointment: null });
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewedDoctors, setReviewedDoctors] = useState({});

  const fetchAppointments = async () => {
    try {
      const token = localStorage.getItem("doctodays_token");
      const res = await axios.get(`${API_URL}/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppointments(res.data);
      
      // Check which doctors have been reviewed
      const reviewed = {};
      for (const apt of res.data) {
        if (apt.status === "completed" || (apt.status === "confirmed" && new Date(apt.date) < new Date())) {
          try {
            const checkRes = await axios.get(`${API_URL}/reviews/check/${apt.doctor_id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            reviewed[apt.doctor_id] = checkRes.data.has_reviewed;
          } catch {
            reviewed[apt.doctor_id] = false;
          }
        }
      }
      setReviewedDoctors(reviewed);
    } catch (err) {
      console.error("Fetch appointments error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate("/login?redirect=/dashboard");
      return;
    }
    fetchAppointments();
  }, [user, navigate]);

  const handleCancel = async (aptId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler ce rendez-vous ?")) return;
    try {
      const token = localStorage.getItem("doctodays_token");
      await axios.patch(`${API_URL}/appointments/${aptId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAppointments();
    } catch (err) {
      alert("Erreur lors de l'annulation");
    }
  };

  const openReviewModal = (apt) => {
    setReviewForm({ rating: 5, comment: "" });
    setReviewModal({ open: true, appointment: apt });
  };

  const submitReview = async () => {
    if (!reviewModal.appointment) return;
    setSubmittingReview(true);
    try {
      const token = localStorage.getItem("doctodays_token");
      await axios.post(`${API_URL}/reviews`, {
        doctor_id: reviewModal.appointment.doctor_id,
        rating: reviewForm.rating,
        comment: reviewForm.comment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviewModal({ open: false, appointment: null });
      setReviewedDoctors(prev => ({ ...prev, [reviewModal.appointment.doctor_id]: true }));
      alert("Merci pour votre avis !");
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de l'envoi de l'avis");
    } finally {
      setSubmittingReview(false);
    }
  };

  const upcomingAppointments = appointments.filter(a => a.status === "confirmed" && new Date(a.date) >= new Date());
  const pastAppointments = appointments.filter(a => a.status !== "confirmed" || new Date(a.date) < new Date());
  const completedAppointments = appointments.filter(a => 
    (a.status === "completed" || (a.status === "confirmed" && new Date(a.date) < new Date())) && 
    a.status !== "cancelled"
  );

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Bonjour, {user.first_name} !</h1>
          <p className="text-slate-600">Gérez vos rendez-vous médicaux</p>
        </div>

        {/* GDPR data control — quick access */}
        <div className="bg-gradient-to-br from-emerald-50 to-slate-50 border border-emerald-100 rounded-xl p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="gdpr-banner">
          <span className="inline-flex w-9 h-9 rounded-full bg-white items-center justify-center shrink-0">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
          </span>
          <div className="flex-1 min-w-[180px]">
            <p className="text-sm font-semibold text-slate-900">Vos données restent les vôtres.</p>
            <p className="text-xs text-slate-600">Téléchargez tout ce que nous savons sur vous (article 20 RGPD).</p>
          </div>
          <button
            onClick={async () => {
              try {
                const token = localStorage.getItem("doctodays_token");
                const res = await axios.get(`${API_URL}/account/me/export`, {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: "blob",
                });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
                const link = document.createElement("a");
                link.href = url;
                link.download = `doctodays-export-${user.id?.slice(0, 8) || "data"}.zip`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
              } catch (err) {
                alert("Téléchargement impossible pour le moment. Réessayez plus tard.");
              }
            }}
            className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
            data-testid="gdpr-export-btn"
          >
            Exporter mes données
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "RDV à venir", value: upcomingAppointments.length, color: "emerald" },
            { label: "RDV passés", value: pastAppointments.length, color: "slate" },
            { label: "Annulés", value: appointments.filter(a => a.status === "cancelled").length, color: "red" },
            { label: "Total", value: appointments.length, color: "blue" },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl p-4 border border-slate-100">
              <p className="text-slate-500 text-sm">{stat.label}</p>
              <p className={`text-2xl font-bold text-${stat.color}-600`}>{stat.value}</p>
            </div>
          ))}
        </div>

        <MyCallbackRequests />

        {/* Upcoming Appointments */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Rendez-vous à venir</h2>
          {loading ? (
            <div className="bg-white rounded-xl p-8 animate-pulse">
              <div className="h-4 bg-slate-200 rounded w-1/3 mb-2" />
              <div className="h-4 bg-slate-200 rounded w-1/4" />
            </div>
          ) : upcomingAppointments.length > 0 ? (
            <div className="space-y-4">
              {upcomingAppointments.map(apt => (
                <div key={apt.id} className="bg-white rounded-xl p-6 border border-slate-100" data-testid={`appointment-${apt.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{apt.doctor_name}</h3>
                        <p className="text-emerald-600 text-sm">{apt.doctor_specialty}</p>
                        {apt.consultation_type === "telephone" && (
                          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-sky-50 text-sky-700 border border-sky-200 rounded-full text-xs font-medium" data-testid={`phone-badge-${apt.id}`}>
                            <Phone className="w-3 h-3" /> Par téléphone — le praticien vous appellera
                          </span>
                        )}
                        {(apt.doctor_address || apt.doctor_city) && (
                          <p className="text-slate-500 text-xs mt-1">
                            {[apt.doctor_address, apt.doctor_city].filter(Boolean).join(", ")}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="text-right">
                        <p className="font-medium text-slate-900">
                          {new Date(apt.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
                        </p>
                        <p className="text-slate-600">{apt.time}</p>
                      </div>
                      <DirectionsButton address={apt.doctor_address} city={apt.doctor_city} />
                      <CopyAddressButton address={apt.doctor_address} city={apt.doctor_city} />
                      <Link
                        to={`/messages/${apt.doctor_id}`}
                        className="px-3 py-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors flex items-center gap-1.5 text-sm font-medium"
                        data-testid={`message-${apt.id}`}
                        title="Envoyer un message au praticien"
                      >
                        <MessageCircle className="w-4 h-4" /> Message
                      </Link>
                      <button
                        onClick={() => handleCancel(apt.id)}
                        className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        data-testid={`cancel-${apt.id}`}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl p-8 text-center border border-slate-100">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">Aucun rendez-vous à venir</p>
              <Link to="/search" className="text-emerald-600 font-medium hover:underline">
                Prendre un rendez-vous
              </Link>
            </div>
          )}
        </div>

        {/* Consultations to Review */}
        {completedAppointments.filter(apt => !reviewedDoctors[apt.doctor_id]).length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Donnez votre avis
            </h2>
            <div className="space-y-3">
              {completedAppointments
                .filter(apt => !reviewedDoctors[apt.doctor_id])
                .slice(0, 3)
                .map(apt => (
                  <div key={apt.id} className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-slate-800">{apt.doctor_name}</h3>
                        <p className="text-sm text-slate-600">
                          Consultation du {new Date(apt.date).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                      <button
                        onClick={() => openReviewModal(apt)}
                        className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-2 font-medium"
                        data-testid={`review-btn-${apt.id}`}
                      >
                        <Star className="w-4 h-4" /> Noter
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Past Appointments */}
        {pastAppointments.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Historique</h2>
            <div className="space-y-3">
              {pastAppointments.slice(0, 5).map(apt => (
                <div key={apt.id} className="bg-white rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <h3 className="font-medium text-slate-700">{apt.doctor_name}</h3>
                        <p className="text-sm text-slate-500">{apt.doctor_specialty}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-slate-600">
                          {new Date(apt.date).toLocaleDateString("fr-FR")} à {apt.time}
                        </p>
                        <span className={`text-xs font-medium ${apt.status === "cancelled" ? "text-red-500" : "text-emerald-600"}`}>
                          {apt.status === "cancelled" ? "Annulé" : "Terminé"}
                        </span>
                      </div>
                      {apt.status !== "cancelled" && reviewedDoctors[apt.doctor_id] && (
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Noté
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Review Modal */}
      <AnimatePresence>
        {reviewModal.open && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              data-testid="review-modal"
            >
              <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-orange-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">Donnez votre avis</h3>
                    <p className="text-sm text-slate-600">{reviewModal.appointment?.doctor_name}</p>
                  </div>
                  <button 
                    onClick={() => setReviewModal({ open: false, appointment: null })}
                    className="p-2 hover:bg-slate-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-5 space-y-5">
                {/* Star Rating */}
                <div className="text-center">
                  <p className="text-slate-600 mb-3">Comment évaluez-vous cette consultation ?</p>
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setReviewForm(f => ({ ...f, rating: star }))}
                        className="p-1 transition-transform hover:scale-110"
                        data-testid={`star-${star}`}
                      >
                        <Star 
                          className={`w-10 h-10 ${
                            star <= reviewForm.rating 
                              ? "fill-amber-400 text-amber-400" 
                              : "text-slate-200 hover:text-amber-200"
                          }`} 
                        />
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-slate-500 mt-2">
                    {reviewForm.rating === 1 && "Très insatisfait"}
                    {reviewForm.rating === 2 && "Insatisfait"}
                    {reviewForm.rating === 3 && "Correct"}
                    {reviewForm.rating === 4 && "Satisfait"}
                    {reviewForm.rating === 5 && "Très satisfait"}
                  </p>
                </div>

                {/* Comment */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Votre commentaire (optionnel)
                  </label>
                  <textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                    placeholder="Partagez votre expérience..."
                    rows={4}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-amber-500 outline-none resize-none"
                    data-testid="review-comment"
                  />
                </div>

                {/* Submit */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setReviewModal({ open: false, appointment: null })}
                    className="flex-1 px-4 py-3 border border-slate-200 rounded-xl hover:bg-slate-50 font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={submitReview}
                    disabled={submittingReview}
                    className="flex-1 px-4 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="submit-review"
                  >
                    {submittingReview ? "Envoi..." : <><Star className="w-4 h-4" /> Publier</>}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== APP ====================
import {
  PractitionerProvider,
  PractitionerLoginPage,
  PractitionerDashboardPage,
  PractitionerPlanningPage,
  PractitionerProfilePage,
  PractitionerForgotPasswordPage,
  PractitionerResetPasswordPage,
  usePractitioner
} from "./PractitionerPages";

import PractitionerBillingPage from "./pages/PractitionerBillingPage";

import {
  AdminProvider,
  AdminLoginPage,
  AdminDashboardPage,
  AdminForgotPasswordPage,
  AdminResetPasswordPage
} from "./AdminPages";
import { AdminBusinessCardsPage } from "./AdminBusinessCardsPage";
import { AdminMedicalKeywordsPage } from "./AdminMedicalKeywordsPage";

// ===== Messages wrappers (inject currentUserId from role-specific context) =====
const PatientMessagesWrapper = () => {
  const { user } = useAuth();
  if (!user) return null;
  return <MessagesPage basePath="/messages" currentUserId={user.id} />;
};
const PractitionerMessagesWrapper = () => {
  const { practitioner } = usePractitioner();
  if (!practitioner) return null;
  return <MessagesPage basePath="/practitioner/messages" currentUserId={practitioner.id} />;
};
const PractitionerBillingWrapper = () => {
  const { token } = usePractitioner();
  return <PractitionerBillingPage token={token} />;
};

function App() {
  return (
    <BrowserRouter>
      <CookieBanner />
      <Routes>
        {/* Admin Routes */}
        <Route path="/admin/*" element={
          <AdminProvider>
            <Routes>
              <Route path="/login" element={<AdminLoginPage />} />
              <Route path="/forgot-password" element={<AdminForgotPasswordPage />} />
              <Route path="/reset-password" element={<AdminResetPasswordPage />} />
              <Route path="/dashboard" element={<AdminDashboardPage />} />
              <Route path="/business-cards" element={<AdminBusinessCardsPage />} />
              <Route path="/medical-keywords" element={<AdminMedicalKeywordsPage />} />
              <Route path="*" element={<AdminLoginPage />} />
            </Routes>
          </AdminProvider>
        } />
        
        {/* Practitioner Routes */}
        <Route path="/practitioner/*" element={
          <PractitionerProvider>
            <Routes>
              <Route path="/login" element={<PractitionerLoginPage />} />
              <Route path="/forgot-password" element={<PractitionerForgotPasswordPage />} />
              <Route path="/reset-password" element={<PractitionerResetPasswordPage />} />
              <Route path="/dashboard" element={<PractitionerDashboardPage />} />
              <Route path="/planning" element={<PractitionerPlanningPage />} />
              <Route path="/profile" element={<PractitionerProfilePage />} />
              <Route path="/messages" element={<PractitionerMessagesWrapper />} />
              <Route path="/messages/:otherUserId" element={<PractitionerMessagesWrapper />} />
              <Route path="/billing" element={<PractitionerBillingWrapper />} />
              <Route path="*" element={<PractitionerLoginPage />} />
            </Routes>
          </PractitionerProvider>
        } />
        
        {/* Patient Routes */}
        <Route path="/*" element={
          <AuthProvider>
            <div className="flex flex-col min-h-screen">
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/mentions-legales" element={<LegalPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/delete-account" element={<DeleteAccountPage />} />
                <Route
                  path="*"
                  element={
                    <>
                      <Navbar />
                      <main className="flex-1">
                        <Routes>
                          <Route path="/" element={<HomePage />} />
                          <Route path="/search" element={<SearchPage />} />
                          <Route path="/doctor/:id" element={<DoctorPage />} />
                          <Route path="/dr/:slug" element={<DoctorBySlugPage />} />
                          <Route path="/dashboard" element={<DashboardPage />} />
                          <Route path="/messages" element={<PatientMessagesWrapper />} />
                          <Route path="/messages/:otherUserId" element={<PatientMessagesWrapper />} />
                        </Routes>
                      </main>
                      <Footer />
                      <SmartAppBanner />
                    </>
                  }
                />
              </Routes>
            </div>
          </AuthProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
